import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.08 },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] as const },
  },
};

const SUGGESTED_QUERIES = [
  'How do you process a creative challenge?',
  'What is your core Creative Engineering stack?',
  'Tell me about your AI & Machine Learning expertise',
  "What does 'Platform Excellence' mean in your workflow?",
];

export const AboutSection: React.FC = () => {
  return (
    <motion.section
      className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-start px-4 pb-10 pt-2 sm:px-5 md:min-h-0 md:flex-1 md:justify-center md:px-6 md:pb-8 md:pt-0 lg:px-8"
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
    >
      <div className="relative w-full min-w-0 max-w-3xl xl:max-w-4xl">
        <motion.div
          variants={ITEM_VARIANTS}
          className="relative min-w-0 overflow-hidden rounded-2xl border border-black/[0.06] bg-white/[0.7] backdrop-blur-sm"
        >
          <div className="px-5 py-7 sm:px-6 sm:py-8 md:px-8 md:py-7 lg:px-10">
            {/* Header: Status + Identity */}
            <motion.div variants={ITEM_VARIANTS} className="mb-4 md:mb-5">
              <div className="flex items-center gap-2 mb-3 md:mb-3">
                <motion.span
                  className="h-2 w-2 rounded-full bg-black/40 shrink-0"
                  animate={{ opacity: [1, 0.3, 1], scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="label">Data-Node Online</span>
              </div>
              <p className="body text-black/90">
                I am the synchronized data-node of Amandine Quellec. I possess
                her project knowledge, technical expertise, and design
                philosophy.
              </p>
            </motion.div>

            {/* Philosophy */}
            <motion.div
              variants={ITEM_VARIANTS}
              className="mb-5 md:mb-5 pl-4 border-l-2 border-black/10"
            >
              <p className="body">
                Creative Technologist & Rapid Prototyper. I bridge technical
                innovation and raw emotion. Rooted in a &apos;tinkering&apos;
                process from Gobelins, I orchestrate high-performance systems
                where technology disappears so the human story can speak for
                itself.
              </p>
            </motion.div>

            {/* Query interface */}
            <motion.div variants={ITEM_VARIANTS}>
              <p className="label mb-3">
                Ask me anything
              </p>
              <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5">
                {SUGGESTED_QUERIES.map((query) => (
                  <motion.button
                    key={query}
                    type="button"
                    className="body min-h-[44px] min-w-0 w-full rounded-xl border border-black/[0.08] bg-black/[0.04] px-4 py-3 text-left text-black/80 transition-all duration-200 hover:border-black/20 hover:bg-black/[0.06] hover:text-black sm:min-h-0 sm:py-2.5"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    {query}
                  </motion.button>
                ))}
              </div>
              <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-black/[0.08] bg-black/[0.02] px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <ChevronRight
                    size={16}
                    strokeWidth={1.8}
                    className="shrink-0 text-black/45"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    placeholder="Type your question..."
                    disabled
                    className="body min-w-0 flex-1 cursor-not-allowed bg-transparent text-black/60 outline-none placeholder:text-black/50"
                    aria-label="AI command input (disabled)"
                  />
                </div>
                <span className="label shrink-0 self-end sm:self-auto">
                  Execute
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.p
          variants={ITEM_VARIANTS}
          className="label mt-4 md:mt-3 text-center"
        >
          Interactive layer in development
        </motion.p>
      </div>
    </motion.section>
  );
};
